import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { searchIcon } from '../../ui-icons/illustrations';
import { mainTheme } from '../../styles/themes/main-theme';
import { IItemSearchResult } from '../../domain/portal-domain';
import { fetchSearchedItem } from '../../data-providers/fetches';
import { EmptySearchResults } from './empty-search-results';
import { ResultsTable } from './results-table';
import { Spinner } from '../spinner';

const Container = styled.div`
    width: 100%;
    margin: 0;
    margin-bottom: ${mainTheme.containerMargins.small};
    padding: 0;
    box-sizing: border-box;
    overflow: hidden;
`;

const Separator = styled.hr`
    width: 100%;
    border: none;
    border-top: 2px solid ${mainTheme.colors.separator};
    margin: ${mainTheme.containerMargins.small} 0;
`;

const Title = styled.div`
    font-size: ${mainTheme.fontSizes.normal};
    font-weight: 400;
    color: ${mainTheme.colors.lightGrey};
    margin-bottom: ${mainTheme.containerMargins.extrasmall};
`;

const SearchInputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

const SearchInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    padding: ${mainTheme.containerMargins.extrasmall} ${mainTheme.containerMargins.big} ${mainTheme.containerMargins.extrasmall}
        ${mainTheme.containerMargins.small};
    border: 1px solid ${mainTheme.colors.lightBlue};
    border-radius: 4px;
    font-size: ${mainTheme.fontSizes.normal};
    outline: none;

    &::placeholder {
        color: ${mainTheme.colors.inactiveInput};
    }

    &:focus {
        border-color: ${mainTheme.colors.bridgePrimary};
    }
`;

const SearchIconWrapper = styled.span`
    position: absolute;
    right: ${mainTheme.containerMargins.small};
    cursor: pointer;
    display: flex;
    align-items: center;
    color: ${mainTheme.colors.lightBlue};
`;

const SearchResultsContainer = styled.div`
    width: 100%;
    max-width: 100%;
    max-height: ${mainTheme.heights.globalSearchResultsMax};
    overflow-x: auto;
    overflow-y: auto;
    background: ${mainTheme.colors.white};
    border: 1px solid ${mainTheme.colors.mediumGrey};
    border-radius: 4px;
    margin-top: ${mainTheme.containerMargins.small};
    margin-bottom: ${mainTheme.containerMargins.extrasmall};
    box-sizing: border-box;
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${mainTheme.containerMargins.big};
    color: ${mainTheme.colors.lightGrey};
    font-size: ${mainTheme.fontSizes.normal};
`;

interface EquipGlobalItemSearchProps {
    hasAccess: boolean;
}

/**
 * Component that is only visible to users who have access to Equip 2.0 application.
 */
export const EquipGlobalItemSearch: React.FC<EquipGlobalItemSearchProps> = ({ hasAccess }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searchResults, setSearchResults] = useState<IItemSearchResult[]>([]);

    if (!hasAccess) {
        return null;
    }

    const handleSearch = async () => {
        if (searchQuery.trim() && searchQuery.trim().length >= 3) {
            setShowResults(true);
            setIsLoading(true);
            try {
                const results = await fetchSearchedItem(searchQuery);
                setSearchResults(results);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setShowResults(false);
    };

    return (
        <Container>
            <Separator />
            <Title>Global search Equip 2.0</Title>
            <SearchInputWrapper>
                <SearchInput
                    type="text"
                    placeholder="Search items globally (min. 3 characters)..."
                    value={searchQuery}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                />
                <SearchIconWrapper onClick={handleSearch}>{searchIcon}</SearchIconWrapper>
            </SearchInputWrapper>
            {showResults && (
                <SearchResultsContainer>
                    {isLoading ? (
                        <LoadingWrapper>
                            <Spinner text="Loading..." styles={{ size: 50, thickness: 8 }} />
                        </LoadingWrapper>
                    ) : searchResults.length === 0 ? (
                        <EmptySearchResults />
                    ) : (
                        <ResultsTable results={searchResults} searchQuery={searchQuery} />
                    )}
                </SearchResultsContainer>
            )}
        </Container>
    );
};
